import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Eye, EyeOff, Loader2, Lock, LogIn, User } from 'lucide-react';
import { ApiService } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { rememberPortal } from '../utils/customDomain';

const DEFAULT_COLOR = '#2563eb';

/** Event-branded sign-in for invited team members: login ID and password only. */
const TeamPortalPage = ({ eventId: eventIdProp, viaCustomDomain = false }) => {
  const params = useParams();
  const eventId = eventIdProp || params.eventId;
  const navigate = useNavigate();
  const { startSession, isAuthenticated, user, loading: authLoading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['teamPortal', eventId],
    queryFn: () => ApiService.getTeamPortal(eventId),
    enabled: !!eventId,
    staleTime: 5 * 60 * 1000,
  });

  const portal = data?.portal || {};
  const available = !!data && (portal.enabled || viaCustomDomain);
  const rawColor = /^#[0-9a-f]{3}$/i.test(portal.primaryColor || '')
    ? `#${portal.primaryColor.slice(1).split('').map((c) => c + c).join('')}`
    : portal.primaryColor;
  const color = /^#[0-9a-f]{6}$/i.test(rawColor || '') ? rawColor : DEFAULT_COLOR;
  const title = portal.title || data?.eventName || 'Team sign-in';
  const logo = portal.logoUrl || data?.eventLogo;
  const destination = `/admin/events/${data?.slug || eventId}/registrations`;

  useEffect(() => {
    if (data?.eventName) document.title = `${title} · Team sign-in`;
  }, [data?.eventName, title]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      setError('Enter your login ID and password.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const response = await ApiService.eventLogin(eventId, { email: email.trim(), password });
      startSession(response.token, response.user);
      rememberPortal(viaCustomDomain ? null : eventId);
      navigate(destination, { replace: true });
    } catch (err) {
      const message = err?.message || '';
      setError(/credential|password|authentication|access/i.test(message)
        ? 'Login ID or password is incorrect, or this account has no access to this event.'
        : message || 'Could not sign in. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" aria-label="Loading" />
      </div>
    );
  }

  if (isError || !available) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-sm rounded-2xl border bg-white p-6 text-center shadow-sm">
          <Lock className="mx-auto mb-3 h-10 w-10 text-gray-400" />
          <h1 className="text-lg font-semibold text-gray-900">Team sign-in isn't available</h1>
          <p className="mt-2 text-sm text-gray-600">
            Ask the event organiser for the correct sign-in link.
          </p>
        </div>
      </div>
    );
  }

  const contain = portal.bannerFit === 'CONTAIN';

  return (
    <div className="min-h-screen bg-gray-50 lg:grid lg:grid-cols-[minmax(0,1.15fr)_minmax(420px,1fr)]">
      <div
        className="relative h-48 overflow-hidden sm:h-64 lg:h-auto lg:min-h-screen"
        style={{ background: `linear-gradient(135deg, ${color}, ${color}cc)` }}
      >
        {portal.bannerUrl ? (
          <img
            src={portal.bannerUrl}
            alt=""
            className={`absolute inset-0 h-full w-full ${contain ? 'object-contain p-6 sm:p-10' : 'object-cover object-center'}`}
          />
        ) : (
          <div className="absolute inset-0 opacity-20 [background-image:radial-gradient(circle_at_1px_1px,white_1px,transparent_0)] [background-size:22px_22px]" />
        )}
        <div className={`absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent ${contain ? "h-1/3" : "h-2/3"}`} />
        <div className="absolute inset-x-0 bottom-0 p-5 text-white sm:p-8 lg:p-12">
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/80">Team access</p>
          <h1 className="mt-1 line-clamp-2 text-2xl font-bold leading-tight sm:text-3xl lg:text-4xl">{title}</h1>
          {portal.subtitle && <p className="mt-2 max-w-xl text-sm text-white/90 sm:text-base">{portal.subtitle}</p>}
        </div>
      </div>

      <div className="flex items-start justify-center px-4 py-8 sm:py-12 lg:items-center lg:px-10">
        <div className="w-full max-w-sm">
          {logo && (
            <div className="mb-6 flex justify-center">
              <img src={logo} alt={data?.eventName || 'Event logo'} className="max-h-20 w-auto max-w-[220px] rounded-md object-contain" />
            </div>
          )}
          <div className="rounded-2xl border bg-white p-6 shadow-lg shadow-gray-200/60 sm:p-8">
            <h2 className="text-xl font-semibold text-gray-900">Sign in to your team account</h2>
            <p className="mt-1 text-sm text-gray-500">{data?.eventName}</p>

            {isAuthenticated && !authLoading && user && (
              <button
                type="button"
                onClick={() => navigate(destination)}
                className="mt-4 w-full rounded-lg border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Continue as {user.email}
              </button>
            )}

            <form onSubmit={submit} className="mt-5 space-y-4" noValidate>
              <div>
                <label htmlFor="portal-login" className="block text-sm font-medium text-gray-700">Login ID</label>
                <div className="relative mt-1">
                  <User className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    id="portal-login"
                    type="email"
                    inputMode="email"
                    autoComplete="username"
                    autoCapitalize="none"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 py-2.5 pl-9 pr-3 text-base sm:text-sm focus:outline-none focus:ring-2"
                    style={{ '--tw-ring-color': color }}
                    placeholder="you@example.com"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="portal-password" className="block text-sm font-medium text-gray-700">Password</label>
                <div className="relative mt-1">
                  <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    id="portal-password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 py-2.5 pl-9 pr-10 text-base sm:text-sm focus:outline-none focus:ring-2"
                    style={{ '--tw-ring-color': color }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-gray-400 hover:text-gray-600"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {error && (
                <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-90 disabled:opacity-60"
                style={{ backgroundColor: color }}
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogIn className="h-4 w-4" />}
                {submitting ? 'Signing in…' : 'Sign in'}
              </button>
            </form>

            <p className="mt-4 text-xs text-gray-500">
              {portal.helpText || 'Forgot your password? Ask the event admin to set a new one for you.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamPortalPage;
